import React from "react";
import { useTranslation } from "react-i18next";
import IosShareIcon from "@mui/icons-material/IosShare";
import { formatCurrency } from "../../../utils/util";

interface PaymentSuccessCardProps {
  data: {
    amount: number;
    dashFee: number;
    total: number;
    paymentInfo: {
      paymentMethod: string;
      cardSuffix: string;
    };
    driver: {
      driverName: string;
      licensePlate: string;
      vehicleMake: string;
      vehicleModel: string;
    };
    createdAt: string;
  };
}

const PaymentSuccessCard: React.FC<PaymentSuccessCardProps> = ({ data }) => {
  const { t } = useTranslation("paymentPage");

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  };

  const getPaymentMethodDisplay = (method: string, suffix: string) => {
    const methodMap: { [key: string]: string } = {
      APPLE_PAY: t("applePay"),
      GOOGLE_PAY: t("googlePay"),
      CREDIT_CARD: "Credit Card",
    };
    return `${methodMap[method] || method} ${suffix}`;
  };

  const getPaymentMethodIcon = (method: string) => {
    const methodIcons: { [key: string]: string } = {
      APPLE_PAY: "/icons8-apple_pay.svg",
      GOOGLE_PAY: "/icons8-google_pay.svg",
      CREDIT_CARD: "/icons8-credit_card.svg",
    };
    return (
      <img
        // style={{ width: "2.5rem", height: "2.5rem", objectFit: "contain" }}
        className="w-[2.5rem] h-[2.5rem] object-contain mx-4"
        src={methodIcons[method]}
      />
    );
  };

  return (
    <>
  <div className="bg-white rounded-lg shadow-sm mx-4 mt-6 mb-0 p-6">
      {/* Success Icon and Message */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
          <img src="/green-check.svg" alt="Success" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          {t("paymentSuccessful")}
        </h2>
        <p className="text-gray-600">
          {formatDate(data.createdAt)}, {formatTime(data.createdAt)}
        </p>
      </div>

      {/* Payment Method and Total */}
      <div className="flex items-center justify-between py-4 border-b border-gray-200">
        <div className="flex items-center">
          <div className="w-8 h-8 rounded flex items-center justify-center mr-3">
            {getPaymentMethodIcon(data.paymentInfo.paymentMethod)}
          </div>
          <span className="text-gray-900 font-semibold">
            {getPaymentMethodDisplay(
              data.paymentInfo.paymentMethod,
              data.paymentInfo.cardSuffix,
            )}
          </span>
        </div>
        <span className="text-2xl font-bold text-gray-900">
          {formatCurrency(data.total)}
        </span>
      </div>

      {/* Fare Breakdown */}
      <div className="py-4 space-y-3">
        <div className="flex justify-between">
          <span className="text-gray-600">{t("taxiFare")}</span>
          <span className="text-gray-900">{formatCurrency(data.amount)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t("platformFee")}</span>
          <span className="text-gray-900">{formatCurrency(data.dashFee)}</span>
        </div>
      </div>

      {/* Driver Info */}
      <div className="pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {data.driver.licensePlate}
            </h3>
            <p className="text-gray-600">
              {data.driver.vehicleMake} {data.driver.vehicleModel}
            </p>
            <p className="text-gray-600">{data.driver.driverName}</p>
          </div>
          <img src="/mifa-icon.svg" alt="Taxi Icon" className="w-20 h-20" />
        </div>
      </div>

      {/* App Download Section */}
      <div className="mt-8 bg-blue-50 rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-center">
          <img
            src="/App_store_1024_1x.svg"
            alt="App Store"
            className="w-12 h-12 mr-4"
          />
          <div>
            <p className="font-medium text-gray-900">{t("getTheApp")}</p>
            <p className="text-sm text-gray-600">{t("anywhereAnytime")}</p>
          </div>
        </div>
        <button className="text-blue-600 font-medium">
          <a
            href={
              process.env.REACT_APP_DASH_TAXI_DOWNLOAD_URL ||
              "https://l.d-ash.com/p0cu2"
            }
            className="text-base font-semibold text-primary-600 hover:underline whitespace-nowrap"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("download")}
          </a>
        </button>
      </div>
    </div>

      {/* Sticky Share Link Button */}
      <div
        className="fixed bottom-0 left-0 w-full bg-white px-4 py-4 z-50 shadow md:fixed md:left-0 md:bottom-0 md:w-full md:px-0 md:py-8"
        style={{ left: 0, right: 0, bottom: 0 }}
      >
        <div className="w-full flex justify-center">
          <div className="w-full md:max-w-[600px]">
            <button
              className="w-full bg-brand-blue text-white text-base font-semibold py-3 rounded-full flex items-center justify-center gap-2"
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: t("paymentSuccessful"),
                    text: 'Receipt Link: ',
                    url: window.location.href,
                  });
                } else {
                  window.navigator.clipboard.writeText(window.location.href);
                  alert(window.location.href);
                }
              }}
            >
              <IosShareIcon className="w-5 h-5" />
              {t("paymentConfirmation_shareButton")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentSuccessCard;
